import { useMemo, useCallback, useState, useEffect } from 'react';
import { cancelDelayUntilIdle, delayUntilIdle } from '../helpers/delayUntilIdle';
import { useGetAssociatedObjectsPagedQuery } from '../../__generated__/chirp/com/hubspot/card/associated/objects/rpc/AssociatedObjectsGatewayRpc.hooks';
import { getObjectFromContext } from '../context/getObjectsFromContext';
import { TOPIC_NAMES } from 'crm-message-bus/types/MessageTopics';
import { useSendCrmMessageTopic } from 'crm-message-bus/useSendCrmMessageTopic';
import { normalizeTypeId } from 'customer-data-objects/constants/ObjectTypeIds';
import isPortalSpecificObjectType from 'customer-data-objects/crmObject/isPortalSpecificObjectType';
import { CrmLogger } from 'customer-data-tracking/loggers';
import { useGetHasFeature } from 'crm-cards-sdk/hooks/useGetHasFeature';
import { useGetIsUngated } from 'crm-cards-sdk/hooks/useGetIsUngated';
import { useGetIsScoped } from 'crm-cards-sdk/hooks/useGetIsScoped';
import { getSortConfigurationDefinition } from '../helpers/sortHelpers';
import Raven from 'raven-js';
import { getRequiredPropertyNames } from '../constants/RequiredPropertyNames';
import { useUserCustomization } from './useUserCustomization';
export const useFetchAssociatedObjectsCardRedesignData = (props, cardContext) => {
  const {
    isCollapsed
  } = props.cardData;
  const [shouldDeferDataRequests, setShouldDeferDataRequests] = useState(Boolean(isCollapsed));
  const [total, setTotal] = useState();
  const [localSortConfiguration, setLocalSortConfiguration] = useState(() => getSortConfigurationDefinition({
    sortConfiguration: props.cardData.configuration.sortConfiguration
  }));
  useEffect(() => {
    if (!isCollapsed) {
      setShouldDeferDataRequests(false);
      return;
    }
    const idleCallbackId = delayUntilIdle(() => {
      setShouldDeferDataRequests(false);
    });
    return () => {
      if (idleCallbackId !== undefined) {
        cancelDelayUntilIdle(idleCallbackId);
      }
    };
  }, [isCollapsed]);
  const {
    propertySource
  } = cardContext;
  const {
    objectTypeId,
    objectId
  } = getObjectFromContext(cardContext);
  const {
    toObjectTypeId
  } = props.cardData.configuration;
  const isUngated = useGetIsUngated();
  const hasScope = useGetIsScoped();
  const hasFeature = useGetHasFeature();
  const {
    updateSortConfiguration
  } = useUserCustomization({
    cardId: props.cardData.definition.cardId,
    objectTypeId
  });
  const requiredPropertyNames = getRequiredPropertyNames(toObjectTypeId, props.cardData.configuration.propertyNames);
  const [initialSortConfiguration] = useState(() => getSortConfigurationDefinition({
    sortConfiguration: props.cardData.configuration.sortConfiguration
  }).map(sort => Object.assign({}, sort, {
    missing: '_last'
  })));
  const {
    data,
    loading,
    refetch
  } = useGetAssociatedObjectsPagedQuery({
    variables: {
      fromObjectTypeId: objectTypeId,
      fromObjectId: objectId,
      toObjectTypeId,
      offset: 0,
      pageLength: 5,
      isPrimaryFirst: true,
      sorts: initialSortConfiguration,
      query: '',
      propertyNames: requiredPropertyNames
    }
  });
  useEffect(() => {
    if ((data === null || data === void 0 ? void 0 : data.total) !== undefined) {
      setTotal(data.total);
    }
  }, [data === null || data === void 0 ? void 0 : data.total]);

  // Account for the poll interval and stopping after 6s https://git.hubteam.com/HubSpot/crm-records/issues/1482
  const handleStartPolling = useCallback(() => {}, []);

  // How does the update query work with DFC, is it stored as a separate entry? https://git.hubteam.com/HubSpot/crm-records/issues/1482
  const handleViewMore = useCallback(() => {}, []);

  // Implement UPDATE_OBJECT and UPDATE_ASSOCIATION listeners https://git.hubteam.com/HubSpot/crm-records/issues/1482

  const propertySourceInfo = useMemo(() => {
    return {
      propertySource
    };
  }, [propertySource]);
  const sendPreviewObjectMessage = useSendCrmMessageTopic(TOPIC_NAMES.PREVIEW_OBJECT);
  const setPreviewConfig = useCallback(config => {
    if (config) {
      const normalizedToObjectTypeId = normalizeTypeId(config.objectTypeId);
      const normalizedFromObjectTypeId = normalizeTypeId(objectTypeId);
      CrmLogger.log('recordInteractions', {
        toObjectTypeId: isPortalSpecificObjectType(normalizedToObjectTypeId) ? 'CUSTOM_OBJECT' : normalizedToObjectTypeId,
        fromObjectTypeId: isPortalSpecificObjectType(normalizedFromObjectTypeId) ? 'CUSTOM_OBJECT' : normalizedFromObjectTypeId,
        action: 'Clicked preview button'
      });
      sendPreviewObjectMessage({
        objectTypeId: normalizeTypeId(config.objectTypeId),
        objectId: `${config.objectId}`
      });
    }
  }, [objectTypeId, sendPreviewObjectMessage]);
  const sendUpdateObject = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_OBJECT);
  const sendObjectCreated = useSendCrmMessageTopic(TOPIC_NAMES.OBJECT_CREATED);
  const sendUpdateAssociation = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_ASSOCIATIONS);
  const sendOpenActivityCreator = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_ACTIVITY_CREATOR);
  const sendOpenCallWidget = useSendCrmMessageTopic(TOPIC_NAMES.OPEN_CALL_WIDGET);
  const sendUpdateCardConfiguration = useSendCrmMessageTopic(TOPIC_NAMES.UPDATE_CARD_CONFIGURATION);
  const handleSearchQueryChange = useCallback(query => {
    refetch({
      query,
      isPrimaryFirst: query.trim() === ''
    }).catch(err => {
      Raven.captureException(err);
    });
  }, [refetch]);
  const handleSortConfigurationChange = useCallback(sortConfiguration => {
    setLocalSortConfiguration(sortConfiguration);
    refetch({
      sorts: sortConfiguration
    }).catch(err => {
      Raven.captureException(err);
    });
    const userSortConfig = sortConfiguration.map(sort => ({
      property: sort.property,
      // Type assertion needed as we have two non-identical 'order' types (GQL and CHIRP) which represent the same value
      direction: sort.order
    }));
    updateSortConfiguration(userSortConfig).then(() => {
      sendUpdateCardConfiguration({
        cardId: props.cardData.definition.cardId,
        cardType: props.cardData.definition.cardType
      });
    }).catch(() => {
      // Do nothing: Error logging handled in useUserCustomization.ts
    });
  }, [refetch, updateSortConfiguration, sendUpdateCardConfiguration, props.cardData.definition.cardId, props.cardData.definition.cardType]);
  const handleEmailClick = useCallback((recipientObjectTypeId, recipientId) => {
    sendOpenActivityCreator({
      interactionType: 'email',
      objectId: Number(recipientId),
      objectTypeId: recipientObjectTypeId
    });
  }, [sendOpenActivityCreator]);
  const handlePhoneClick = useCallback((recipientObjectTypeId, recipientId) => {
    sendOpenCallWidget({
      interactionType: 'call',
      objectId: Number(recipientId),
      objectTypeId: recipientObjectTypeId
    });
    sendOpenCallWidget({
      interactionType: 'call',
      objectId: Number(recipientId),
      objectTypeId: recipientObjectTypeId
    });
  }, [sendOpenCallWidget]);
  const handleObjectUpdate = useCallback(({
    properties
  }) => {
    sendUpdateObject({
      propertyNames: properties.map((_val, key) => key).toArray(),
      objectTypeId,
      objectId: String(objectId)
    });
  }, [objectId, objectTypeId, sendUpdateObject]);
  const handleAssociationUpdate = useCallback(({
    objectId: updatedObjectId,
    objectType: updatedObjectType,
    associationObjectType
  }) => {
    sendUpdateAssociation({
      objectId: String(updatedObjectId),
      objectTypeId: normalizeTypeId(updatedObjectType),
      toObjectTypeId: normalizeTypeId(associationObjectType)
    });
  }, [sendUpdateAssociation]);
  const handleObjectCreate = useCallback(({
    associationType,
    objectId: createdObjectId,
    addAnother
  }) => {
    sendObjectCreated({
      objectId: String(createdObjectId),
      objectTypeId: normalizeTypeId(associationType),
      preventRedirect: !!addAnother
    });
  }, [sendObjectCreated]);
  return useMemo(() => ({
    associationDefinition: data === null || data === void 0 ? void 0 : data.associationDefinition,
    cardId: props.cardData.definition.cardId,
    hasFeature,
    hasMore: data === null || data === void 0 ? void 0 : data.hasMore,
    hasScope,
    isLoading: shouldDeferDataRequests || loading,
    isUngated,
    fromObjectId: objectId,
    fromObjectTypeId: objectTypeId,
    objectPermissions: data === null || data === void 0 ? void 0 : data.objectPermissions,
    offset: data === null || data === void 0 ? void 0 : data.offset,
    objectTypeDefinition: data === null || data === void 0 ? void 0 : data.objectTypeDefinition,
    onAssociationUpdate: handleAssociationUpdate,
    onEmailClick: handleEmailClick,
    onObjectCreate: handleObjectCreate,
    onObjectUpdate: handleObjectUpdate,
    onPhoneClick: handlePhoneClick,
    onPreview: setPreviewConfig,
    onRefetchLegacySidebar: () => {},
    onStartPolling: handleStartPolling,
    onViewMore: handleViewMore,
    onSearchQueryChange: handleSearchQueryChange,
    onSortConfigurationChange: handleSortConfigurationChange,
    propertyDefinitions: data === null || data === void 0 ? void 0 : data.propertyDefinitions,
    propertyNames: props.cardData.configuration.propertyNames,
    propertySource: propertySourceInfo,
    readOnly: Boolean(cardContext.readOnly),
    refetchObjectData: () => {},
    results: data === null || data === void 0 ? void 0 : data.results,
    sortConfiguration: localSortConfiguration,
    title: props.cardData.definition.cardTitle,
    toObjectTypeId,
    total,
    usageTracker: props.usageTracker,
    // TODO: look at real permissions, chat with Gav about this https://git.hubteam.com/HubSpot/crm-records/issues/1482
    userPermissions: {
      currentUserCanEdit: true,
      currentUserCanEditAssociations: true
    },
    userPermission: props.cardData.configuration.userPermission
  }), [data === null || data === void 0 ? void 0 : data.associationDefinition, data === null || data === void 0 ? void 0 : data.hasMore, data === null || data === void 0 ? void 0 : data.objectPermissions, data === null || data === void 0 ? void 0 : data.offset, data === null || data === void 0 ? void 0 : data.objectTypeDefinition, data === null || data === void 0 ? void 0 : data.propertyDefinitions, data === null || data === void 0 ? void 0 : data.results, props.cardData.definition.cardId, props.cardData.definition.cardTitle, props.cardData.configuration.propertyNames, props.cardData.configuration.userPermission, props.usageTracker, hasFeature, hasScope, shouldDeferDataRequests, loading, isUngated, objectId, objectTypeId, handleAssociationUpdate, handleEmailClick, handleObjectCreate, handleObjectUpdate, handlePhoneClick, setPreviewConfig, handleStartPolling, handleViewMore, handleSearchQueryChange, handleSortConfigurationChange, propertySourceInfo, cardContext.readOnly, localSortConfiguration, toObjectTypeId, total]);
};